import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { cx, delay } from '@/utils';
import SearchearIcon from '@common/Icons/SearchearIcon';
import SvgUserCircle from '@common/Icons/UserCircle';
import { useLayoutContext } from '@common/Layout/LayoutProvider/LayoutContext';
import { Spinner } from '@material-tailwind/react';
import { ROUTES_CLIENT } from '@utils/constants';
import useUserStore, { useModalSearchStore } from '@utils/store/store';
import Cookies from 'js-cookie';

import GovCo2025 from '@/components/common/Icons/GovCo2025';

import WrapperGrayScale from '../../../common/Accessibility/WrapperGrayScale';
import CustomLink from '../../../common/CustomLink';
import {
  PageBlockStyles,
  PageLink,
  User,
} from '../../../common/Providers/GraphqlProvider/generated';

const GovCoHeader = () => {
  const [loading, setLoading] = useState(false);
  const { user, remove: removeUser } = useUserStore();
  const { onShow } = useModalSearchStore();
  const { replace, push } = useRouter();
  const {
    layout: { headerBar, searchBlock },
  } = useLayoutContext();

  const onLogout = async () => {
    setLoading(true);
    removeUser();
    Cookies.remove('token');
    await delay(1000);
    await replace(ROUTES_CLIENT.LOGIN);
    setLoading(false);
  };

  const pageLink = headerBar?.govcoLink as PageLink | undefined;

  const isSearchEnabled =
    searchBlock && !searchBlock.isDisabled && searchBlock.pageBlockStyle === PageBlockStyles.A;

  const searchButton = (
    <>
      <button
        className={cx('rounded-full border-2 border-white bg-marine p-2', {
          'md:block': isSearchEnabled,
          'md:hidden': !isSearchEnabled,
        })}
        onClick={() => {
          onShow(true);
        }}
      >
        <SearchearIcon width={14} height={14} className={'text-white'} />
      </button>
    </>
  );

  return (
    <nav role="navigation" aria-label="Cabecera gubernamental" className="flex min-h-[44px] w-full">
      <WrapperGrayScale className="flex flex-row bg-marine md:min-h-[48px]">
        <div className="inner flex w-full flex-row items-center justify-between">
          <div className="flex flex-1 flex-row items-center justify-center md:justify-start">
            <div className="flex flex-row items-center justify-center">
              {headerBar?.hasGovcoLogo && (
                <CustomLink
                  pageLink={pageLink}
                  className="relative flex h-[31px] w-[120px] items-center"
                >
                  <GovCo2025
                    width={100}
                    height={24}
                    variant="white"
                    className="translate-y-[2px]"
                  />
                </CustomLink>
              )}
            </div>
          </div>

          <div className="flex w-auto flex-row items-center space-x-20">
            {user ? (
              <AuthenticatedSection
                loading={loading}
                onLogout={onLogout}
                push={push}
                user={user}
                searchButton={searchButton}
              />
            ) : (
              <PublicSection
                hasSignin={headerBar?.hasSignin}
                signinName={headerBar?.signinName}
                hasSignup={headerBar?.hasSignup}
                signupName={headerBar?.signupName}
                searchButton={searchButton}
              />
            )}
          </div>
        </div>
      </WrapperGrayScale>
    </nav>
  );
};

type PublicSectionProps = {
  hasSignin?: boolean | null;
  signinName?: string | null;
  hasSignup?: boolean | null;
  signupName?: string | null;
  searchButton?: React.ReactNode;
};

const PublicSection = ({
  hasSignin,
  signinName,
  hasSignup,
  signupName,
  searchButton,
}: PublicSectionProps) => {
  return (
    <div className="flex-row items-center space-x-4 font-workSans text-[14px] font-medium not-italic leading-[21px] text-white md:flex">
      {searchButton}
      {hasSignin && (
        <Link className="hidden items-center md:flex" href={'/login'}>
          {signinName}
        </Link>
      )}
      {hasSignup && (
        <>
          <div className="hidden h-[24px] w-[1px] items-center bg-white md:flex" />
          <Link className="hidden items-center md:flex" href={'/signup'}>
            {signupName}
          </Link>
        </>
      )}
    </div>
  );
};

type AuthenticatedSectionProps = {
  push: (url: string) => void;
  user: User;
  onLogout: () => void;
  loading: boolean;
  searchButton?: React.ReactNode;
};

const AuthenticatedSection = ({
  push,
  user,
  onLogout,
  loading,
  searchButton,
}: AuthenticatedSectionProps) => {
  return (
    <div className={'items-center justify-center gap-[16px] md:flex'}>
      {searchButton}
      <div
        onClick={() => push(ROUTES_CLIENT.DASHBOARD)}
        className={'hidden cursor-pointer items-center justify-center gap-[10px] md:flex'}
      >
        <SvgUserCircle />
        <div className={'flex items-baseline justify-center'}>
          <div>
            <div
              className={
                'font-workSans text-[14px] font-semibold not-italic leading-[16px] text-white'
              }
            >
              {user?.fullName}
            </div>
            <div
              className={
                'font-workSans text-[14px] font-normal not-italic leading-[16px] text-white'
              }
            >
              {user.email}
            </div>
          </div>
        </div>
      </div>
      <div className={'hidden items-center justify-center gap-[16px] md:flex'}>
        <div className={'h-[24px] w-[1px] bg-white'} />
        <button
          className={cx(
            'relative font-workSans text-[14px] font-medium not-italic leading-[21px] text-white',
            { ['opacity-50']: loading }
          )}
          onClick={onLogout}
          disabled={loading}
        >
          {'Cerrar sesión'}
          {loading && <Spinner className={'absolute top-0 flex w-full items-center'} />}
        </button>
      </div>
    </div>
  );
};

export default GovCoHeader;
